import { ClientApi, ClientDataType } from "@/api/ClientApi";
import { CURRENCIES_CODE_IDS, INVOICE_CONTADO_TYPE_CODE, INVOICE_CREDIT_TYPE_CODE } from "@utils/Constants";
import { nextAvailableCode } from "@utils/import/resolveImportRows";
import { mapParsedLine } from "./mapLines";
import { parseEDocumentXml } from "./parseEDocumentXml";
import { ReadEDocumentFile } from "./readEDocumentFiles";
import {
    normalizeDocumentNumber,
    normalizeIdentification,
    normalizeProductCode,
} from "./loadEDocumentImportContext";
import {
    EDocumentImportContext,
    EDocumentImportDefinition,
    ParsedEDocument,
    PendingClient,
    ReferenceableDocument,
    ResolvedEDocument,
    ResolvedEDocumentLine,
} from "./types";

/** `CondicionVenta` 01 es contado; el resto se trata como credito. */
export const CASH_SALE_CONDITION = "01";

/**
 * Busca en el sistema el documento que el comprobante referencia.
 *
 * `InformacionReferencia.Numero` puede traer la clave de 50 digitos o el consecutivo de 20, asi
 * que se prueban los dos indices. Una nota declara hasta 10 referencias (a la factura, a otra
 * nota, a un contrato...) y basta con que una resuelva.
 */
const resolveReference = (
    parsed: ParsedEDocument,
    context: EDocumentImportContext
): { document?: ReferenceableDocument; searched: string } => {
    const searched: string[] = [];

    for (const reference of parsed.referencias) {
        const numero = normalizeDocumentNumber(reference.numero);
        if (!numero) continue;

        searched.push(reference.numero);
        const document =
            context.referenceableByKey.get(numero) || context.referenceableBySequence.get(numero);
        if (document) return { document, searched: reference.numero };
    }

    return { searched: searched.join(", ") };
};

const resolveCurrency = (
    parsed: ParsedEDocument,
    context: EDocumentImportContext
): { currencyId: string; message?: string } => {
    const currencyCode = parsed.resumen.codigoMoneda;
    const currencyNumber = CURRENCIES_CODE_IDS[currencyCode];
    const currency = context.currencies.find((item) => `${item.currency_id}` === `${currencyNumber}`);

    if (currency?.id) return { currencyId: currency.id };

    return {
        currencyId: `${context.defaultCurrency?.id || ""}`,
        message: `La moneda ${currencyCode} no esta registrada en el sistema, se asigno la moneda por defecto`,
    };
};

/** Datos del cliente a crear a partir del `Receptor`, sin el codigo (se asigna despues). */
const buildPendingClient = (parsed: ParsedEDocument): PendingClient | undefined => {
    const receptor = parsed.receptor;
    if (!receptor?.identificacion.numero || !receptor.nombre) return undefined;

    return {
        nombre: receptor.nombre,
        nombre_comercial: receptor.nombreComercial,
        tipo_identificacion: receptor.identificacion.tipo,
        identificacion: receptor.identificacion.numero,
        email: receptor.correo,
        telefono1_cd: receptor.telefonoCodigoPais,
        telefono1: receptor.telefono,
        direccion: receptor.direccion,
        activitycode: parsed.codigoActividadReceptor,
        tipo: Number(
            parsed.condicionVenta === CASH_SALE_CONDITION
                ? INVOICE_CONTADO_TYPE_CODE
                : INVOICE_CREDIT_TYPE_CODE
        ),
        active: true,
    };
};

const resolveLines = (
    parsed: ParsedEDocument,
    context: EDocumentImportContext
): ResolvedEDocumentLine[] =>
    parsed.lineas.map((line, index) => {
        const data = mapParsedLine(line, index);
        const match = context.productsByCode.get(normalizeProductCode(line.codigo));

        return {
            data,
            productId: match?.id,
            // Sin codigo en el XML o sin coincidencia en inventario, la linea viaja con toda
            // su informacion fiscal y sin `productId`: es exactamente un producto temporal.
            temporal: !match,
        };
    });

/**
 * Convierte los XML leidos en documentos listos para importar.
 *
 * Resuelve contra los datos del tenant (cliente por identificacion, producto por codigo),
 * detecta duplicados por la clave de Hacienda y marca lo que no se puede importar.
 *
 * Es agnostico del tipo de comprobante: el filtro por raiz ya lo aplico `readEDocumentFiles`
 * con las raices que declara la definicion, y lo unico que cambia entre facturas y notas es si
 * la definicion exige o no una referencia a un documento ya cargado.
 */
export const resolveEDocuments = (params: {
    files: ReadEDocumentFile[];
    context: EDocumentImportContext;
    definition: EDocumentImportDefinition;
    /**
     * Vuelve importables los comprobantes que ya estan cargados en vez de omitirlos. El id del
     * documento se deriva de la clave de Hacienda, asi que reescribe el mismo registro en lugar
     * de duplicarlo: es la via para reparar una carga anterior volviendo a subir el archivo.
     */
    reprocessExisting?: boolean;
}): ResolvedEDocument[] => {
    const { files, context, definition, reprocessExisting } = params;
    const requirement = definition.requiresReference;
    // Un mismo comprobante puede venir repetido dentro del zip; el primero gana.
    const seenKeys = new Set<string>();

    return files.map((file) => {
        let parsed: ParsedEDocument;
        try {
            parsed = parseEDocumentXml(file.xml);
        } catch (error) {
            return {
                fileName: file.fileName,
                parsed: {} as ParsedEDocument,
                status: "error",
                messages: [(error as Error).message],
                lines: [],
                currencyId: "",
                exchangeRate: 1,
                missingReference: false,
                action: "skip",
            };
        }

        const messages: string[] = [...parsed.warnings, ...(definition.extraWarnings?.(parsed) || [])];
        const { currencyId, message: currencyMessage } = resolveCurrency(parsed, context);
        if (currencyMessage) messages.push(currencyMessage);

        const emitter = normalizeIdentification(parsed.emisor.identificacion.numero);
        const tenant = normalizeIdentification(context.tenantIdentification);
        const reference = requirement ? resolveReference(parsed, context) : undefined;

        let status: ResolvedEDocument["status"] = "new";
        let action: ResolvedEDocument["action"] = "create";
        let missingReference = false;

        if (!parsed.clave || !parsed.numeroConsecutivo) {
            messages.push("El comprobante no trae clave o consecutivo");
            status = "error";
            action = "skip";
        } else if (tenant && emitter && emitter !== tenant) {
            messages.push(`El comprobante lo emitio otra cedula (${parsed.emisor.identificacion.numero})`);
            status = "error";
            action = "skip";
        } else if (!parsed.lineas.length) {
            messages.push("El comprobante no trae lineas de detalle");
            status = "error";
            action = "skip";
        } else if (seenKeys.has(parsed.clave)) {
            // Repetido dentro del mismo archivo: escribirlo dos veces no aporta nada, ni siquiera
            // reprocesando, porque las dos copias generan el mismo item.
            messages.push("El archivo trae este comprobante repetido");
            status = "duplicate";
            action = "skip";
        } else if (context.existingKeys.has(parsed.clave)) {
            // El duplicado gana sobre la referencia faltante: si el documento ya esta cargado,
            // lo util es decir "duplicado", no mandar al usuario a buscar una factura.
            if (reprocessExisting) {
                messages.push("Ya existe: se reescribira con los datos del XML");
                status = "reprocess";
                action = "create";
            } else {
                messages.push("Ya existe un documento con esta clave");
                status = "duplicate";
                action = "skip";
            }
        } else if (requirement && !reference?.document) {
            messages.push(requirement.missingMessage(reference?.searched || ""));
            status = "error";
            action = "skip";
            missingReference = true;
        } else if (messages.length) {
            status = "warning";
        }

        if (action === "create") seenKeys.add(parsed.clave);

        // Cuando el comprobante depende de otro documento, el cliente sale de ese documento: una
        // nota pertenece al mismo cliente que su factura, y ademas no trae con que crearlo (el
        // `Receptor` es opcional en su XSD y el modelo no guarda snapshot del cliente).
        const identification = normalizeIdentification(parsed.receptor?.identificacion.numero);
        const existingClient = identification
            ? context.clientsByIdentification.get(identification)
            : undefined;
        const clientId = requirement ? reference?.document?.clientId : existingClient?.id;
        const pendingClient =
            requirement || existingClient ? undefined : buildPendingClient(parsed);

        const lines = resolveLines(parsed, context);

        return {
            fileName: file.fileName,
            parsed,
            status,
            messages,
            lines,
            clientId,
            pendingClient,
            currencyId,
            exchangeRate: parsed.resumen.tipoCambio,
            referencedDocument: reference?.document,
            missingReference,
            action,
        };
    });
};

/** Clientes distintos que hay que crear, deduplicados por identificacion. */
export const collectPendingClients = (documents: ResolvedEDocument[]): PendingClient[] => {
    const pending = new Map<string, PendingClient>();

    documents
        .filter((document) => document.action === "create" && document.pendingClient)
        .forEach((document) => {
            const client = document.pendingClient as PendingClient;
            const key = normalizeIdentification(client.identificacion);
            if (key && !pending.has(key)) pending.set(key, client);
        });

    return [...pending.values()];
};

/**
 * Crea los clientes que el archivo menciona y el tenant todavia no tiene.
 *
 * Se hace desde el navegador (son unos pocos, no cientos) para que `ClientApi` invalide su
 * cache y para que la lambda reciba `clientId` ya resueltos. Mismo patron que
 * `createMissingZones` en la importacion de CSV.
 *
 * Es idempotente por identificacion: si una carga anterior fallo despues de crear parte de los
 * clientes, al reintentar se reutilizan los que ya existen en vez de duplicarlos. Devuelve
 * tanto los creados como los reutilizados, porque ambos hacen falta para ligar los documentos.
 */
export const createMissingClients = async (
    pendingClients: PendingClient[],
    existingClients: ClientDataType[]
): Promise<ClientDataType[]> => {
    if (!pendingClients.length) return [];

    const existingByIdentification = new Map<string, ClientDataType>();
    existingClients.forEach((client) => {
        const identification = normalizeIdentification(client.identificacion);
        if (identification && !existingByIdentification.has(identification)) {
            existingByIdentification.set(identification, client);
        }
    });

    // `Client.codigo` es requerido: el consecutivo se calcula una vez y sube en memoria.
    let nextCode = nextAvailableCode(existingClients);
    const resolved: ClientDataType[] = [];

    for (const pending of pendingClients) {
        const identification = normalizeIdentification(pending.identificacion);
        const existing = identification ? existingByIdentification.get(identification) : undefined;

        if (existing) {
            resolved.push(existing);
            continue;
        }

        const client = await ClientApi.create({ ...pending, codigo: `${nextCode}` });
        nextCode += 1;
        resolved.push(client);
        if (identification) existingByIdentification.set(identification, client);
    }

    return resolved;
};

/** Liga los documentos a los clientes recien creados. */
export const applyCreatedClients = (
    documents: ResolvedEDocument[],
    createdClients: ClientDataType[]
): ResolvedEDocument[] => {
    if (!createdClients.length) return documents;

    const index = new Map<string, string>();
    createdClients.forEach((client) => {
        const key = normalizeIdentification(client.identificacion);
        if (key && client.id) index.set(key, client.id);
    });

    return documents.map((document) => {
        if (document.clientId || !document.pendingClient) return document;

        const clientId = index.get(normalizeIdentification(document.pendingClient.identificacion));
        return clientId ? { ...document, clientId, pendingClient: undefined } : document;
    });
};
